/*
 * Edit a configured cake that is already in the cart.
 *
 * WHY THIS EXISTS: a cake in the cart could only be deleted and rebuilt from scratch. A
 * customer who wanted to change one thing went Back, found an empty configurator, gave up on
 * editing, rebuilt the cake, deleted the old one — and its add-on lines stayed behind, so she
 * had to start the whole site over. Microsoft Clarity showed the same struggle more than once.
 *
 * WHERE THE DATA LIVES: in the cart itself. No Supabase, no localStorage. Shopify already
 * persists the cart per visitor, so the cake line carries a hidden `_bh_state` snapshot of the
 * configurator (underscore = hidden at checkout and never printed by the cart templates or
 * QuickOrder). The cart page links to the PDP with `?bh_edit=<_Cake_ID>`,
 * snippets/bakehunters-config.liquid prints the matching snapshot into the page, and this
 * module replays it through the configurator's own handlers.
 *
 * REPLAY, NOT ASSIGNMENT: every field is restored by doing what a customer does — clicking the
 * delivery pill, changing the select, picking the day in <bh-date-picker>. That keeps every
 * rule the configurator enforces in force. A date that has since become unbookable (a closure,
 * a lead time that has passed) is simply not re-selected and the customer picks a new one,
 * instead of an invalid date being smuggled back in.
 */
window.BhCartEdit = {
  /** The cake being edited, or null. Only returns a match for a cake that is in the cart. */
  context(config) {
    const id = new URLSearchParams(window.location.search).get('bh_edit');
    const entry = id && config.cartCakes && config.cartCakes[id];
    if (!entry) return null;
    try {
      // Liquid hands line-item property values over HTML-escaped (" arrives as &quot;).
      const raw = new DOMParser().parseFromString(entry.state, 'text/html').documentElement.textContent;
      return { cakeId: id, saved: JSON.parse(raw), photo: entry.photo || null };
    } catch (e) {
      return null; // a corrupt snapshot must never stop the PDP from working
    }
  },

  /**
   * Everything needed to rebuild this cake: the configurator's state plus the raw value of
   * every visible form field. Fields are captured generically by name so a field added to the
   * PDP later (a new free-text box) round-trips without anyone remembering to update this.
   * Hidden inputs are skipped because they are derived from state; files cannot be serialised.
   */
  snapshot(app) {
    const s = app.state;
    const form = {};
    // form.elements, not querySelectorAll: it also includes controls placed outside the
    // <form> and attached with form="…", so moving a field in the layout cannot drop it.
    Array.from(app.dom.form.elements).forEach((el) => {
      if (!el.name || !el.name.startsWith('properties[')) return;
      if (el.type === 'hidden' || el.type === 'file') return;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      if (el.value !== '') form[el.name] = el.value;
    });
    const addonText = {};
    document.querySelectorAll('.bh-addon-row').forEach((row) => {
      const input = row.querySelector('.bh-addon-text-input');
      if (input && input.value) addonText[row.dataset.handle] = input.value;
    });
    return JSON.stringify({
      v: 1,
      state: {
        shape: s.shape, sizeHandle: s.sizeHandle, sponge: s.sponge, fillings: s.fillings,
        colorHandle: s.colorHandle, message: s.message, addons: s.addons || {},
        deliveryMethod: s.deliveryMethod, deliveryZone: s.deliveryZone, date: s.date
      },
      form,
      addonText,
      qty: parseInt(app.dom.qtyInput && app.dom.qtyInput.value) || 1
    });
  },

  /** Replays the non-state parts of a snapshot. Runs after renderInitialState(). */
  hydrate(app, saved) {
    const fire = (el, ...types) => types.forEach((t) => el.dispatchEvent(new Event(t, { bubbles: true })));
    const st = saved.state || {};

    // 1. Delivery method first: it decides whether the zone and time-slot fields exist.
    if (st.deliveryMethod) {
      const pill = document.querySelector(`.bh-delivery-pill[data-method="${st.deliveryMethod}"]`);
      if (pill) pill.click();
    }

    // 2. Every other form field, by name. Date and time slot wait for step 3: the slot list
    //    is rebuilt from the date, so setting it now would be wiped.
    const skip = new Set([app.dom.deliveryDate, app.dom.timeSlotSelect]);
    Object.entries(saved.form || {}).forEach(([name, value]) => {
      Array.from(app.dom.form.elements).filter((el) => el.name === name).forEach((el) => {
        if (skip.has(el)) return;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (el.value !== value) return;
          el.checked = true;
          fire(el, 'change');
        } else {
          el.value = value;
          fire(el, 'input', 'change');
        }
      });
    });

    // Text add-ons (side text etc.) keep their text in state AND in an unnamed input.
    Object.entries(saved.addonText || {}).forEach(([handle, value]) => {
      const input = document.querySelector(`.bh-addon-row[data-handle="${handle}"] .bh-addon-text-input`);
      if (input) { input.value = value; fire(input, 'input'); }
    });

    // 3. Date through the calendar's own select path, so closures and lead times re-apply.
    const picker = app.dom.datePicker;
    if (st.date && picker && typeof picker._select === 'function') {
      const [y, m] = st.date.split('-').map(Number);
      picker.viewYear = y;
      picker.viewMonth = m - 1;
      // No focus: a restored date must not scroll the page to the calendar. The scroll belongs
      // only to the rejected-date branch below, where the customer has to act.
      picker._select(st.date, { focus: false });
      if (!app.state.date) {
        // Rejected (passed, or closed since): show the first bookable month, not the dead one.
        picker._advanceToMinMonth();
        picker.render();
        // Same scroll + pulse + toast as a missing field on add-to-cart, so the customer lands
        // exactly where the one thing they have to change is. Delayed so the page has finished
        // laying out the restored options, or the scroll lands short.
        setTimeout(() => app.flagMissing({
          el: document.querySelector('.bh-datetime') || picker,
          flashEl: picker,
          message: app.t('edit_date_gone')
        }), 450);
      }
      const slot = saved.form && saved.form[app.dom.timeSlotSelect && app.dom.timeSlotSelect.name];
      if (slot && app.dom.timeSlotSelect && app.state.date) {
        app.dom.timeSlotSelect.value = slot;
        fire(app.dom.timeSlotSelect, 'change');
      }
    }

    if (app.dom.qtyInput && saved.qty) app.dom.qtyInput.value = saved.qty;

    // Say what the button will do. "Add to cart" on an edit reads as "this adds a second cake".
    // Only the leading text node changes: the live price is the #FinalPrice span beside it
    // (snippets/cake-block-atc.liquid), which calculateTotal() keeps updating.
    const price = document.getElementById('FinalPrice');
    const text = price && price.previousSibling;
    if (text && text.nodeType === Node.TEXT_NODE && app.t('edit_save') !== 'edit_save') {
      text.textContent = `${app.t('edit_save')} — `;
    }
    app.calculateTotal();
  },

  /** Drops the cake being replaced and every line that belongs to it. */
  async removeCake(cakeId) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await (await fetch(root + 'cart.js')).json();
    const updates = {};
    cart.items.forEach((it) => {
      if (it.properties && it.properties._Cake_ID === cakeId) updates[it.key] = 0;
    });
    if (!Object.keys(updates).length) return;
    await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  }
};
